import { ACCESSORY_STATUS_LABELS, hasPermission } from "@edm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PackageOpen, Plus, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { StatCard } from "@/components/stat-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/lib/api";
import { useMe } from "@/lib/auth";
import {
  type AccessoryRow,
  type AssetRow,
  createResource,
  listResource,
  type MaterialRow,
  useReferenceData,
} from "@/lib/master-data";
import { formatDate } from "@/lib/utils";

const STATUS_VARIANT: Record<string, "default" | "success" | "warning" | "secondary"> = {
  in_stock: "success",
  in_use: "default",
  returned: "warning",
  scrapped: "secondary",
};

export function AccessoriesPage() {
  const { data: me } = useMe();
  const reference = useReferenceData();
  const queryClient = useQueryClient();
  const [inboundOpen, setInboundOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState<AccessoryRow | null>(null);
  const [returnTarget, setReturnTarget] = useState<AccessoryRow | null>(null);
  const [scrapTarget, setScrapTarget] = useState<AccessoryRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    materialId: "",
    quantity: "1",
    price: "",
    inboundDate: new Date().toISOString().slice(0, 10),
    location: "总部 3 号楼 502",
    compatibleModel: "",
  });
  const [assignForm, setAssignForm] = useState({
    targetUserId: "",
    assetId: "",
    location: "总部 3 号楼 502",
  });
  const [returnForm, setReturnForm] = useState({ usable: true, reason: "" });
  const [scrapReason, setScrapReason] = useState("");

  const canManage = Boolean(me && hasPermission(me.permissions, "inventory:manage"));
  const accessoriesQuery = useQuery({
    queryKey: ["accessories"],
    queryFn: () => listResource<AccessoryRow>("/api/v1/accessories"),
  });
  const materialsQuery = useQuery({
    queryKey: ["materials"],
    queryFn: () => listResource<MaterialRow>("/api/v1/materials"),
  });
  const assetsQuery = useQuery({
    queryKey: ["assets"],
    queryFn: () => listResource<AssetRow>("/api/v1/assets"),
  });

  const accessories = accessoriesQuery.data ?? [];
  const materials = materialsQuery.data ?? [];
  const accessoryMaterials = materials.filter(
    (item) => item.category === "accessory" && item.status === "active",
  );
  const materialById = useMemo(
    () => new Map(materials.map((item) => [item.id, item])),
    [materials],
  );
  const users = reference.data?.users ?? [];
  const stats = {
    total: accessories.length,
    inStock: accessories.filter((row) => row.status === "in_stock").length,
    inUse: accessories.filter((row) => row.status === "in_use").length,
    returned: accessories.filter((row) => row.status === "returned").length,
  };

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["accessories"] });
    queryClient.invalidateQueries({ queryKey: ["consumable-stock"] });
  }

  async function inbound() {
    if (!form.materialId) return toast.error("请选择低值易耗物料");
    if (!form.price.trim()) return toast.error("请填写采购单价");
    setBusy(true);
    try {
      await createResource("/api/v1/accessories/inbound", {
        materialId: form.materialId,
        quantity: Number(form.quantity),
        purchaseUnitPriceYuan: Number(form.price),
        inboundDate: form.inboundDate,
        location: form.location || null,
        compatibleModel: form.compatibleModel || null,
        instanceDetails: [],
      });
      toast.success("低值易耗已入库");
      setInboundOpen(false);
      setForm({ ...form, materialId: "", price: "", quantity: "1" });
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "入库失败");
    } finally {
      setBusy(false);
    }
  }

  function openAssign(row: AccessoryRow) {
    setAssignTarget(row);
    setAssignForm({ targetUserId: "", assetId: "", location: row.location ?? "总部 3 号楼 502" });
  }

  async function assign() {
    if (!assignTarget) return;
    if (!assignForm.targetUserId) return toast.error("请选择使用人");
    if (!assignForm.location.trim()) return toast.error("请填写使用位置");
    setBusy(true);
    try {
      await createResource(`/api/v1/accessories/${assignTarget.id}/assign`, {
        targetUserId: assignForm.targetUserId,
        assetId: assignForm.assetId || null,
        location: assignForm.location,
      });
      toast.success("已分配交付");
      setAssignTarget(null);
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "分配失败");
    } finally {
      setBusy(false);
    }
  }

  async function submitReturn() {
    if (!returnTarget) return;
    setBusy(true);
    try {
      await createResource(`/api/v1/accessories/${returnTarget.id}/return`, {
        usable: returnForm.usable,
        reason: returnForm.reason || null,
      });
      toast.success("已登记退回，请返库或报废");
      setReturnTarget(null);
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "退回失败");
    } finally {
      setBusy(false);
    }
  }

  async function restock(row: AccessoryRow) {
    try {
      await createResource(`/api/v1/accessories/${row.id}/restock`, {});
      toast.success("已返库");
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "返库失败");
    }
  }

  async function scrap() {
    if (!scrapTarget) return;
    if (!scrapReason.trim()) return toast.error("请填写报废原因");
    setBusy(true);
    try {
      await createResource(`/api/v1/accessories/${scrapTarget.id}/scrap`, { reason: scrapReason });
      toast.success("已报废");
      setScrapTarget(null);
      setScrapReason("");
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "报废失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="低值易耗"
        description="按实例管理外设与配件：入库、分配交付、退回验收、返库或报废；使用人历史与操作审计完整保留。"
        actions={
          <>
            <Button variant="outline" onClick={() => accessoriesQuery.refetch()}>
              <RefreshCw className="h-4 w-4" />
              刷新
            </Button>
            {canManage ? (
              <Button onClick={() => setInboundOpen(true)}>
                <Plus className="h-4 w-4" />
                登记入库
              </Button>
            ) : null}
          </>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="总量" value={stats.total} icon={PackageOpen} />
        <StatCard label="在库" value={stats.inStock} icon={PackageOpen} accent="success" />
        <StatCard label="使用中" value={stats.inUse} icon={PackageOpen} accent="warning" />
        <StatCard label="退回待检" value={stats.returned} icon={PackageOpen} accent="danger" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>低值易耗列表</CardTitle>
          <CardDescription>退回后由设备管理员确认返库或报废，不直接跳过验收。</CardDescription>
        </CardHeader>
        <CardContent>
          {accessoriesQuery.isLoading ? (
            <div className="space-y-2">
              {["p1", "p2", "p3", "p4"].map((key) => (
                <Skeleton key={key} className="h-11 w-full" />
              ))}
            </div>
          ) : accessories.length === 0 ? (
            <EmptyState title="暂无低值易耗" description="先登记入库，再分配给使用人。" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>编号</TableHead>
                  <TableHead>物料</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>使用人</TableHead>
                  <TableHead>使用位置</TableHead>
                  <TableHead>财务卡片号</TableHead>
                  <TableHead>入库日期</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accessories.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-xs">{row.code}</TableCell>
                    <TableCell>{materialById.get(row.materialId)?.name ?? "—"}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[row.status] ?? "secondary"}>
                        {ACCESSORY_STATUS_LABELS[
                          row.status as keyof typeof ACCESSORY_STATUS_LABELS
                        ] ?? row.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {users.find((user) => user.id === row.holderId)?.displayName ?? "—"}
                    </TableCell>
                    <TableCell>{row.location ?? "—"}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {row.financialCardNo ?? "—"}
                    </TableCell>
                    <TableCell>{formatDate(row.inboundDate)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canManage && row.status === "in_stock" ? (
                          <Button variant="ghost" size="sm" onClick={() => openAssign(row)}>
                            分配
                          </Button>
                        ) : null}
                        {canManage && row.status === "in_use" ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setReturnTarget(row);
                              setReturnForm({ usable: true, reason: "" });
                            }}
                          >
                            退回
                          </Button>
                        ) : null}
                        {canManage && row.status === "returned" ? (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => restock(row)}>
                              返库
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setScrapTarget(row)}>
                              报废
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={inboundOpen} onOpenChange={setInboundOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>低值易耗入库</DialogTitle>
            <DialogDescription>按实例生成编号；财务卡片号可在入库后编辑。</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>物料</Label>
              <select
                value={form.materialId}
                onChange={(event) => setForm({ ...form, materialId: event.target.value })}
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="">请选择低值易耗物料</option>
                {accessoryMaterials.map((material) => (
                  <option key={material.id} value={material.id}>
                    {material.code} · {material.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>数量</Label>
                <Input
                  type="number"
                  min="1"
                  max="200"
                  value={form.quantity}
                  onChange={(event) => setForm({ ...form, quantity: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>单价（元）</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={(event) => setForm({ ...form, price: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>入库日期</Label>
                <Input
                  type="date"
                  value={form.inboundDate}
                  onChange={(event) => setForm({ ...form, inboundDate: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>适配型号</Label>
                <Input
                  value={form.compatibleModel}
                  onChange={(event) => setForm({ ...form, compatibleModel: event.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>存放位置</Label>
              <Input
                value={form.location}
                onChange={(event) => setForm({ ...form, location: event.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInboundOpen(false)}>
              取消
            </Button>
            <Button onClick={inbound} disabled={busy}>
              确认入库
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(assignTarget)} onOpenChange={(open) => !open && setAssignTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>分配低值易耗：{assignTarget?.code}</DialogTitle>
            <DialogDescription>选择使用人并可关联到一台已领用设备。</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>使用人</Label>
              <select
                value={assignForm.targetUserId}
                onChange={(event) =>
                  setAssignForm({ ...assignForm, targetUserId: event.target.value })
                }
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="">请选择</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>关联资产（选填）</Label>
              <select
                value={assignForm.assetId}
                onChange={(event) => setAssignForm({ ...assignForm, assetId: event.target.value })}
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="">不关联</option>
                {(assetsQuery.data ?? [])
                  .filter((asset) => ["assigned", "borrowed"].includes(asset.status))
                  .map((asset) => (
                    <option key={asset.id} value={asset.id}>
                      {asset.assetCode} · {materialById.get(asset.materialId)?.name ?? ""}
                    </option>
                  ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>使用位置</Label>
              <Input
                value={assignForm.location}
                onChange={(event) => setAssignForm({ ...assignForm, location: event.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignTarget(null)}>
              取消
            </Button>
            <Button onClick={assign} disabled={busy}>
              确认分配
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(returnTarget)} onOpenChange={(open) => !open && setReturnTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>退回验收：{returnTarget?.code}</DialogTitle>
            <DialogDescription>登记退回后，由管理员确认返库或报废。</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={returnForm.usable}
                onChange={(event) => setReturnForm({ ...returnForm, usable: event.target.checked })}
                className="h-4 w-4"
              />
              外观/功能可继续使用
            </label>
            <div className="space-y-2">
              <Label>备注</Label>
              <Input
                value={returnForm.reason}
                onChange={(event) => setReturnForm({ ...returnForm, reason: event.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnTarget(null)}>
              取消
            </Button>
            <Button onClick={submitReturn} disabled={busy}>
              确认退回
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(scrapTarget)} onOpenChange={(open) => !open && setScrapTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>报废低值易耗：{scrapTarget?.code}</DialogTitle>
            <DialogDescription>报废后不可恢复，必须填写原因。</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>报废原因</Label>
            <Input value={scrapReason} onChange={(event) => setScrapReason(event.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScrapTarget(null)}>
              取消
            </Button>
            <Button variant="danger" onClick={scrap} disabled={busy}>
              确认报废
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
